import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { buildMyAssignmentItems, getChildAssignmentStatus } from "../../lib/myAssignmentItems";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import {
    getMyAssignedJobs,
    subscribeToMyJobUpdates,
    isUserAssignmentCompleted,
    getUserAssignedQuotes
} from "../../lib/supabase/my-assignments";

import {
    FiSearch, FiX, FiAlertCircle, FiCheckCircle, FiChevronDown,
    FiChevronRight, FiPhone, FiMail, FiUser, FiTool,
    FiRefreshCw, FiClipboard, FiFolder, FiLayers, FiDollarSign,
    FiMapPin, FiMessageSquare
} from "react-icons/fi";
import {
    ArrowDownNarrowWide,
    ArrowUpNarrowWide,
    ArrowDownAZ,
    Flame,
    Home,
    BrickWall,
    Grid2X2,
    Layers,
    PanelTop,
    SquareStack,
} from "lucide-react";
import { GiWoodBeam, GiWoodPile } from "react-icons/gi";

// ─── constants ────────────────────────────────────────────────────────────────

const STORAGE_KEYS = {
    sortBy: 'my_assignments_sortBy',
    hideCompleted: 'my_assignments_hideCompleted',
    searchQuery: 'my_assignments_searchQuery',
    showCancelled: 'my_assignments_showCancelled',
};

const load = (key, def) => {
    try { const v = localStorage.getItem(key); return v !== null ? JSON.parse(v) : def; }
    catch { return def; }
};
const save = (key, val) => {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch { }
};

const STATUS_META = {
    pending_design: { dot: "bg-amber-400" },
    in_design: { dot: "bg-blue-400" },
    design_completed: { dot: "bg-emerald-400" },
    pending_estimation: { dot: "bg-orange-400" },
    in_estimation: { dot: "bg-violet-400" },
    estimation_completed: { dot: "bg-green-400" },
    ready_for_delivery: { dot: "bg-teal-400" },
    completed: { dot: "bg-gray-400" },
};
const getStatusDot = (s) => (STATUS_META[s] || { dot: "bg-gray-300" }).dot;

const SORT_OPTIONS = [
    { value: "newest", label: "Newest First", icon: ArrowDownNarrowWide },
    { value: "oldest", label: "Oldest First", icon: ArrowUpNarrowWide },
    { value: "priority", label: "Priority First", icon: Flame },
    { value: "alphabetical", label: "A → Z", icon: ArrowDownAZ },
];

const QUOTE_ICONS = {
    beams: <GiWoodPile size={10} />,
    flooring_sheets: <SquareStack size={10} />,
    roof: <Home size={10} />,
    wall: <BrickWall size={10} />,
    floor: <Grid2X2 size={10} />,
    timber: <PanelTop size={10} />,
    estimation: <FiDollarSign size={10} />,
};

const QUOTE_LABELS = {
    beams: "Beams",
    flooring_sheets: "Flooring Sheets",
    roof: "Roof",
    wall: "Wall",
    floor: "Floor",
    timber: "Timber",
    estimation: "Est."
};

// ─── tiny helpers ─────────────────────────────────────────────────────────────

const formatDate = (ds) => {
    if (!ds) return "—";
    return new Date(ds).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
};
const formatRelativeTime = (date) => {
    if (!date) return "—";
    const diff = Date.now() - new Date(date);
    const m = Math.floor(diff / 60000), h = Math.floor(m / 60), d = Math.floor(h / 24);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    if (h < 24) return `${h}h ago`;
    if (d < 7) return `${d}d ago`;
    return formatDate(date);
};

const Chip = ({ children, className = "" }) => (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none border ${className}`}>
        {children}
    </span>
);

// ─── main component ───────────────────────────────────────────────────────────

const MyAssignments = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { companySlug } = useParams();

    const [allJobs, setAllJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    // ── filters (persisted) ──
    const [searchQuery, setSearchQuery] = useState(() => load(STORAGE_KEYS.searchQuery, ""));
    const [sortBy, setSortBy] = useState(() => load(STORAGE_KEYS.sortBy, "newest"));
    const [hideCompleted, setHideCompleted] = useState(() => load(STORAGE_KEYS.hideCompleted, false));
    const [openDropdown, setOpenDropdown] = useState(null);
    const [showCancelled, setShowCancelled] = useState(
        () => load(STORAGE_KEYS.showCancelled, false)
    );

    const refSort = useRef(null);

    useEffect(() => { save(STORAGE_KEYS.sortBy, sortBy); }, [sortBy]);
    useEffect(() => { save(STORAGE_KEYS.hideCompleted, hideCompleted); }, [hideCompleted]);
    useEffect(() => { save(STORAGE_KEYS.searchQuery, searchQuery); }, [searchQuery]);
    useEffect(() => {
        save(STORAGE_KEYS.showCancelled, showCancelled);
    }, [showCancelled]);

    // ── close dropdown on outside click ──
    useEffect(() => {
        const handler = (e) => {
            if (!refSort.current?.contains(e.target)) setOpenDropdown(null);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // ── data ──
    const fetchMyAssignments = async () => {
        if (!company?.id || !userProfile?.id) return;
        setIsLoading(true);
        try {
            const data = await getMyAssignedJobs(userProfile.id, company.id);
            setAllJobs(data);
            setLastUpdated(new Date());
        } catch (err) {
            console.error("Error fetching my assignments:", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchMyAssignments();
    }, [company?.id, userProfile?.id]); // eslint-disable-line

    useEffect(() => {
        if (!company?.id) return;
        const channel = subscribeToMyJobUpdates(company.id, () => fetchMyAssignments());
        return () => supabase.removeChannel(channel);
    }, [company?.id]); // eslint-disable-line

    // ── build flat item list (parent + child entries) ──
    //   type 'old'    → old-system job, no badge, navigate to /:companySlug/assignment/:jobId
    //   type 'parent' → new-system job card, "Parent" badge, navigate to /:companySlug/assignment/:jobId
    //   type 'child'  → new-system child card, "Child" badge, navigate to /:companySlug/assignment/:jobId/:quoteId
    // ── filter + sort ──
    const displayItems = (() => {
        let jobs = [...allJobs];

        if (!showCancelled) {
            jobs = jobs.filter(job => !job.is_cancelled);
        }

        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            jobs = jobs.filter(job =>
                job.unique_id?.toLowerCase().includes(q) ||
                job.customer_name?.toLowerCase().includes(q) ||
                job.carpenter_supervisor_name?.toLowerCase().includes(q)
            );
        }

        switch (sortBy) {
            case "newest":
                jobs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                break;
            case "oldest":
                jobs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                break;
            case "priority":
                jobs.sort((a, b) =>
                    ((b.urgent_quote || b.urgent_design) ? 1 : 0) -
                    ((a.urgent_quote || a.urgent_design) ? 1 : 0)
                );
                break;
            case "alphabetical":
                jobs.sort((a, b) => (a.unique_id || "").localeCompare(b.unique_id || ""));
                break;
            default: break;
        }

        const items = buildMyAssignmentItems(jobs, userProfile?.id);

        if (hideCompleted) {
            return items.filter(item => {
                if (item.type === 'child') {
                    return !getChildAssignmentStatus(item.job, item.quote, userProfile?.id).isCompleted;
                }
                // Parent / old-system items: hide when all of the user's assignments are done
                return !isUserAssignmentCompleted(item.job, userProfile?.id);
            });
        }

        return items;
    })();

    const handleNavigate = (item) => {
        if (item.type === 'child') {
            navigate(`/${companySlug}/assignment/${item.job.id}/${item.quote.id}`, {
                state: { from: location.pathname }
            });
        } else {
            navigate(`/${companySlug}/assignment/${item.job.id}`, {
                state: { from: location.pathname }
            });
        }
    };

    const currentSort = SORT_OPTIONS.find(o => o.value === sortBy) || SORT_OPTIONS[0];
    const SortIcon = currentSort.icon;

    // ─── render ─────────────────────────────────────────────────────────────

    const renderItem = (item) => {
        const { type, job, quote } = item;
        const isUrgent = job.urgent_quote || job.urgent_design;
        const childStatus = type === 'child'
            ? getChildAssignmentStatus(job, quote, userProfile?.id)
            : null;
        const isCompleted = childStatus
            ? childStatus.isCompleted
            : isUserAssignmentCompleted(job, userProfile?.id);
        const isCancelled = !!job.is_cancelled;

        return (
            <div
                onClick={() => handleNavigate(item)}
                className={`group border rounded-lg transition-all cursor-pointer
    ${isCancelled
                        ? "bg-red-50 border-red-300 opacity-80 hover:border-red-400"
                        : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm"
                    }`}
            >
                <div className="flex items-stretch min-h-[72px]">
                    {/* Left accent bar */}
                    <div className={`w-1 rounded-l-lg flex-shrink-0 ${isCancelled ? "bg-red-500" : getStatusDot(job.main_status)}`} />

                    {/* Main content */}
                    <div className="flex-1 min-w-0 px-3 py-2.5 flex flex-col gap-1">

                        {/* Row 1 — ID + type + system badges + urgent/done */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-bold text-sm text-gray-900 tracking-tight">
                                {type === 'child' ? quote.child_unique_id : job.unique_id}
                            </span>
                            <span className="text-xs text-gray-400 font-medium">{job.job_type}</span>
                            {isCancelled && (
                                <Chip className="bg-red-100 text-red-700 border-red-200">
                                    <FiAlertCircle size={9} />
                                    Cancelled
                                </Chip>
                            )}
                            {type === 'parent' && (
                                <Chip className="bg-violet-50 text-violet-700 border-violet-200">
                                    <FiFolder size={8} />
                                    Parent
                                </Chip>
                            )}
                            {type === 'child' && (
                                <>
                                    <Chip className="bg-sky-50 text-sky-700 border-sky-200">
                                        <FiLayers size={8} />
                                        Child
                                    </Chip>
                                    <Chip className="bg-gray-100 text-gray-600 border-gray-200">
                                        {QUOTE_ICONS[quote.quote_type]}
                                        {QUOTE_LABELS[quote.quote_type] || quote.quote_type}
                                    </Chip>
                                </>
                            )}

                            {isUrgent && (
                                <Chip className="bg-red-100 text-red-700 border-red-200">
                                    <FiAlertCircle size={9} />
                                    {job.urgent_quote && job.urgent_design ? "Urgent" : job.urgent_quote ? "Urgent Quote" : "Urgent Design"}
                                </Chip>
                            )}
                            {isCompleted && (
                                <Chip className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                    <FiCheckCircle size={9} />
                                    Done
                                </Chip>
                            )}
                        </div>

                        {/* Row 2 — customer + supervisor + address */}
                        <div className="flex items-center gap-4 flex-wrap text-xs">
                            <span className="flex items-center gap-1 font-medium text-gray-700">
                                <FiUser size={11} className="text-gray-400 flex-shrink-0" />
                                {job.customer_name || "—"}
                            </span>
                            {job.carpenter_supervisor_name && (
                                <span className="flex items-center gap-1 text-gray-500">
                                    <FiTool size={11} className="text-gray-400 flex-shrink-0" />
                                    {job.carpenter_supervisor_name}
                                </span>
                            )}
                            {job.customer_address && (
                                <span className="flex items-center gap-1 text-gray-400 max-w-[200px] truncate">
                                    <FiMapPin size={11} className="flex-shrink-0" />
                                    {job.customer_address}
                                </span>
                            )}
                        </div>

                        {/* Notes row */}
                        {(() => {
                            const notes = type === 'child'
                                ? (quote?.editor_note || [])
                                : (job.editor_note || []);
                            if (!notes.length) return null;
                            return (
                                <div className="flex items-start gap-1 flex-wrap">
                                    <FiMessageSquare size={10} className="text-blue-400 flex-shrink-0 mt-0.5" />
                                    {notes.map((note, idx) => (
                                        <span
                                            key={idx}
                                            className="inline-flex items-center text-[10px] text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded max-w-[220px] truncate"
                                            title={note}
                                        >
                                            {note}
                                        </span>
                                    ))}
                                </div>
                            );
                        })()}

                        {/* Row 3 — assigned quote chips (parent + old) OR child status */}
                        {type !== 'child' ? (
                            <div className="flex items-center gap-1 flex-wrap">
                                {getUserAssignedQuotes(job, userProfile?.id).map(q => (
                                    <Chip
                                        key={q.type}
                                        className={
                                            q.isCompleted ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                                : q.isStarted ? "bg-amber-100 text-amber-700 border-amber-200"
                                                    : "bg-gray-100 text-gray-600 border-gray-200"
                                        }
                                    >
                                        {QUOTE_ICONS[q.type]}
                                        {QUOTE_LABELS[q.type] || q.type}
                                        {q.isCompleted && <FiCheckCircle size={8} />}
                                    </Chip>
                                ))}
                            </div>
                        ) : (
                            childStatus && (
                                <div className="flex items-center gap-1">
                                    <Chip className={
                                        childStatus.isCompleted ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                            : childStatus.isStarted ? "bg-amber-100 text-amber-700 border-amber-200"
                                                : "bg-gray-100 text-gray-600 border-gray-200"
                                    }>
                                        {childStatus.isCompleted ? "Completed"
                                            : childStatus.isStarted ? "In Progress"
                                                : "Not Started"}
                                    </Chip>
                                </div>
                            )
                        )}
                    </div>

                    {/* Right side — date + hover actions + chevron */}
                    <div className="flex flex-col items-end justify-between py-2.5 pr-3 pl-2 flex-shrink-0 gap-2">
                        <span className="text-[10px] text-gray-400 whitespace-nowrap">{formatDate(job.created_at)}</span>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {job.customer_number && (
                                <a
                                    href={`tel:${job.customer_number}`}
                                    onClick={e => e.stopPropagation()}
                                    className="text-gray-400 hover:text-blue-600 transition-colors"
                                    title="Call"
                                >
                                    <FiPhone size={13} />
                                </a>
                            )}
                            {job.customer_mail && (
                                <a
                                    href={`mailto:${job.customer_mail}`}
                                    onClick={e => e.stopPropagation()}
                                    className="text-gray-400 hover:text-blue-600 transition-colors"
                                    title="Email"
                                >
                                    <FiMail size={13} />
                                </a>
                            )}
                            {job.customer_address && (
                                <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.customer_address)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={e => e.stopPropagation()}
                                    className="text-gray-400 hover:text-blue-600 transition-colors"
                                    title="Open in Maps"
                                >
                                    <FiMapPin size={13} />
                                </a>
                            )}
                        </div>
                        <FiChevronRight size={14} className="text-gray-300 group-hover:text-gray-500 transition-colors" />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="mobile-list-page my-assignments-page flex flex-col h-full min-h-0">

            {/* ── Page header ── */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 leading-tight">My Assignments</h1>
                    {lastUpdated && (
                        <p className="text-xs text-gray-400 mt-0.5">Updated {formatRelativeTime(lastUpdated)}</p>
                    )}
                </div>
                <button
                    onClick={fetchMyAssignments}
                    className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    title="Refresh"
                >
                    <FiRefreshCw size={14} />
                </button>
            </div>

            {/* ── Filter bar ── */}
            <div className="bg-white border border-gray-200 rounded-lg flex-shrink-0 mb-3">

                {/* Row 1 — search + sort */}
                <div className="flex items-center gap-2 px-4 py-2.5 flex-wrap">
                    <div className="relative flex-1 min-w-[180px]">
                        <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                        <input
                            type="text"
                            placeholder="Search job ID, customer, supervisor…"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 placeholder-gray-400"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery("")}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <FiX size={13} />
                            </button>
                        )}
                    </div>

                    <div className="relative" ref={refSort}>
                        <button
                            onClick={() => setOpenDropdown(p => p === "sort" ? null : "sort")}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-white text-gray-700 border-gray-200 hover:border-gray-300 transition-all"
                        >
                            <SortIcon className="w-3 h-3" />
                            <span className="hidden sm:inline">{currentSort.label}</span>
                            <FiChevronDown className={`w-3 h-3 transition-transform ${openDropdown === "sort" ? "rotate-180" : ""}`} />
                        </button>
                        {openDropdown === "sort" && (
                            <div className="absolute right-0 mt-1.5 w-44 bg-white border border-gray-200 rounded-lg shadow-xl z-[60] overflow-hidden">
                                <div className="p-3 border-b border-gray-100">
                                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Sort By</span>
                                </div>
                                <div className="p-1.5 space-y-0.5">
                                    {SORT_OPTIONS.map(opt => {
                                        const Icon = opt.icon;
                                        return (
                                            <button
                                                key={opt.value}
                                                onClick={() => { setSortBy(opt.value); setOpenDropdown(null); }}
                                                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs font-medium transition-colors
                                                    ${sortBy === opt.value ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50"}`}
                                            >
                                                <Icon className="w-3.5 h-3.5 shrink-0" />
                                                <span className="flex-1 text-left">{opt.label}</span>
                                                {sortBy === opt.value && <FiCheckCircle className="w-3.5 h-3.5 opacity-70" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Row 2 — show filter */}
                <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex items-center gap-2 flex-wrap rounded-b-lg">
                    <span className="text-xs font-medium text-gray-500">Show:</span>
                    <button
                        onClick={() => setHideCompleted(false)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
                            ${!hideCompleted
                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                            }`}
                    >
                        All Jobs
                    </button>
                    <button
                        onClick={() => setHideCompleted(true)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
                            ${hideCompleted
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                            }`}
                    >
                        <FiCheckCircle size={11} />
                        Hide Completed
                    </button>
                    <button
                        onClick={() => setShowCancelled(v => !v)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all border
        ${showCancelled
                                ? "bg-red-50 text-red-700 border-red-200"
                                : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                            }`}
                    >
                        <FiAlertCircle size={11} />
                        {showCancelled ? "Showing Cancelled" : "Show Cancelled"}
                    </button>
                    <span className="ml-auto text-xs text-gray-500">
                        <span className="font-semibold text-gray-800">{displayItems.length}</span> items
                        {" · "}
                        <span className="font-semibold text-gray-800">{allJobs.length}</span> jobs
                    </span>
                </div>
            </div>

            {/* ── List ── */}
            <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-24 gap-3">
                        <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-gray-500">Loading assignments…</p>
                    </div>
                ) : displayItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <FiClipboard className="text-gray-400" size={20} />
                        </div>
                        <p className="text-sm font-medium text-gray-700 mb-1">No assignments found</p>
                        <p className="text-xs text-gray-400">
                            {searchQuery || hideCompleted ? "Try adjusting filters or search" : "No assignments yet"}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-1.5 pb-6">
                        {displayItems.map((item) => {
                            const key = item.type === 'child'
                                ? `${item.job.id}-child-${item.quote.id}`
                                : `${item.job.id}-${item.type}`;
                            return (
                                <React.Fragment key={key}>
                                    {renderItem(item)}
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyAssignments;
